import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { workspaceSettingsService, WorkspaceSettings } from '@/api/services/workspaceSettings.service';
import { AppDispatch, RootState } from '@/store/store';

interface WorkspaceSettingsState {
  settings: WorkspaceSettings | null;
  loading: boolean;
  error: string | null;
  updateLoading: boolean;
  updateError: string | null;
  lastFetched: number | null;
}

const initialState: WorkspaceSettingsState = {
  settings: null,
  loading: false,
  error: null,
  updateLoading: false,
  updateError: null,
  lastFetched: null,
};

export const getWorkspaceSettings = createAsyncThunk<
  WorkspaceSettings,
  void,
  { rejectValue: string }
>('workspaceSettings/getWorkspaceSettings', async (_, { rejectWithValue }) => {
  try {
    return await workspaceSettingsService.getWorkspaceSettings();
  } catch (err: unknown) {
    const error = err as Error;
    return rejectWithValue(error.message ?? 'Failed to fetch workspace settings');
  }
});

export const updateWorkspaceSettings = createAsyncThunk<
  boolean,
  Partial<WorkspaceSettings>,
  { rejectValue: string }
>('workspaceSettings/updateWorkspaceSettings', async (partialSettings: Partial<WorkspaceSettings>, { rejectWithValue }) => {
  try {
    if (partialSettings?.excludedTeamsFromCopilot?.length === 0) {
      partialSettings.excludedTeamsFromCopilot = null;
    }
    return await workspaceSettingsService.updateWorkspaceSettings(
      partialSettings
    );
  } catch (err: unknown) {
    const error = err as Error;
    return rejectWithValue(error.message ?? 'Failed to update workspace settings');
  }
});

const FIVE_MINUTES = 5 * 60 * 1000;

export const maybeFetchWorkspaceSettings = () => (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const state = getState();
  const lastFetched = selectWorkspaceSettingsLastFetched(state);

  const shouldFetch =
    !lastFetched || (Date.now() - lastFetched) > FIVE_MINUTES;

  if (shouldFetch) {
    return dispatch(getWorkspaceSettings());
  }
};

const workspaceSettingsSlice = createSlice({
  name: 'workspaceSettings',
  initialState,
  reducers: {
    clearWorkspaceSettingsError: (state) => {
      state.error = null;
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWorkspaceSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getWorkspaceSettings.fulfilled, (state, action: PayloadAction<WorkspaceSettings>) => {
        state.loading = false;
        state.settings = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(getWorkspaceSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch workspace settings';
      })
      .addCase(updateWorkspaceSettings.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateWorkspaceSettings.fulfilled, (state, action) => {
        state.updateLoading = false;
        // Update the local settings with the new values that were sent to the server
        if (action.meta.arg) {
          state.settings = {
            ...state.settings,
            ...action.meta.arg,
          };
        }
      })
      .addCase(updateWorkspaceSettings.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload as string || 'Failed to update workspace settings';
      });
  },
});

export const selectWorkspaceSettings = (state: { workspaceSettings: WorkspaceSettingsState }) => state.workspaceSettings.settings;
export const selectWorkspaceSettingsLoading = (state: { workspaceSettings: WorkspaceSettingsState }) => state.workspaceSettings.loading;
export const selectWorkspaceSettingsError = (state: { workspaceSettings: WorkspaceSettingsState }) => state.workspaceSettings.error;
export const selectWorkspaceSettingsUpdateLoading = (state: { workspaceSettings: WorkspaceSettingsState }) => state.workspaceSettings.updateLoading;
export const selectWorkspaceSettingsUpdateError = (state: { workspaceSettings: WorkspaceSettingsState }) => state.workspaceSettings.updateError;
export const selectWorkspaceSettingsLastFetched = (state: { workspaceSettings: WorkspaceSettingsState }) =>
  state.workspaceSettings.lastFetched;

export const { clearWorkspaceSettingsError } = workspaceSettingsSlice.actions;

export default workspaceSettingsSlice.reducer;